import React, { useState, useEffect } from "react";
import { Avatar, Box, IconButton, Menu, Tooltip, ListItemAvatar, ListItemText, Typography } from "@mui/material";
import EditIcon from "../../assets/common/edit-icon.svg";
import CustomDropdown from "../Common/CustomDropdown";
import AddPersonIcon from "../../assets/interaction-detail-page/Add_person_icon.svg";
import "../../styles/interactionDetailsPage.css";
import { capitalizeFirstLetter, getAvatarColor, getAvatarInitials } from "../../utils/utils";

type Props = {
  email: string | null;
  name?: string;
  participant?: { name?: string; email: string; pictureUrl?: string };
  attendeeParticipants: any[];
  canEdit: boolean;
  row: any;
  rowAction: any;
  noBorder?: boolean;
  onUpdate?: (row: any, email: string | null, removeFields?: string[]) => void;
  showCrossIcon?: boolean;
  fieldName?: "coach" | "sponsor";
  highlightedCell?: boolean; 
  autoOpenAssigneeDropdown?: boolean;
  removeHighlight?: () => void;
  showName?: boolean;
  dashed?: boolean;
  hideActionIcon?: boolean;
  showEditIcon?: boolean;
  updateAssigneeKey?: "profileOrgRelationshipId" | "email";
};

const ActionItemsAssigneeCell: React.FC<Props> = ({
  email,
  name,
  participant,
  attendeeParticipants,
  canEdit,
  row,
  rowAction,
  onUpdate,
  showCrossIcon,
  fieldName,
  highlightedCell,
  autoOpenAssigneeDropdown,
  removeHighlight,
  showName = false,
  dashed = false,
  hideActionIcon = false,
  showEditIcon = false,
  updateAssigneeKey = "profileOrgRelationshipId",
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const open = Boolean(anchorEl);
  const hasError = highlightedCell;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (canEdit) setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setIsHovered(false);
  };

const handleSelect = (user: any) => {
  if (onUpdate) {
    onUpdate(row, user ? user?.[updateAssigneeKey] ?? null : null);
  } else if (rowAction?.updateAssigneeCallback) {
    rowAction.updateAssigneeCallback(row, user ? user?.[updateAssigneeKey] ?? null : null);
  }
  setIsHovered(false);
  handleClose();
};

const cellRef = React.useRef<HTMLDivElement | null>(null);

useEffect(() => {
  if (autoOpenAssigneeDropdown && canEdit && !open && cellRef.current) {
    const timer = setTimeout(() => {
      setAnchorEl(cellRef.current);
    }, 100);
    return () => clearTimeout(timer);
  }
}, [autoOpenAssigneeDropdown, canEdit]);

  useEffect(() => {
    if (!highlightedCell) return;
  
    const handleOutside = (e: MouseEvent) => {
      if (cellRef.current && !cellRef.current.contains(e.target as Node)) {
        removeHighlight?.();   // ⬅ remove highlight
      }
    };
  
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [highlightedCell, removeHighlight]);  

  const normalizeParticipant = (p: any) => ({
    ...p,
    name: p?.name ? capitalizeFirstLetter(p.name) : "",
    initials: getAvatarInitials(p?.name || p?.email),
    pictureUrl: p?.pictureUrl || null,
  });

    // case: no email + cannot edit
  if (email === null && !canEdit) {
    return <Box sx={{ width: "100%", pl: "14px" }}>-</Box>;
  }
  return (
<Box
  ref={cellRef}
  data-row-id={row.id}
  className={`assignee-cell-wrapper 
    ${dashed ? "assignee-cell-wrapper--dashed" : ""} 
    ${open ? "assignee-cell-wrapper--active" : ""} 
    ${hasError ? "error-border" : ""} 
  ${!canEdit ? "no-hover" : ""}`}
  onClick={handleOpen}
  onMouseEnter={() => setIsHovered(true)}
  onMouseLeave={() => setIsHovered(false)}
  sx={{
    width: dashed ? "fit-content" : undefined,
    border: dashed ? "1px dashed #d6d6e2" : undefined,
    borderRadius: dashed ? "6px" : undefined,
    padding: dashed ? "2px 2px" : undefined,
    backgroundColor: dashed ? "#ffffff" : undefined,
    "&:hover": dashed
      ? { border: "1px dashed #d6d6e2" }
      : undefined,
  }}
>
      {canEdit ? (
        <>
          {email ? (
            <Box
              className={`cell-hover-parent`}
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                padding: "4px 8px",
                cursor: canEdit ? "pointer" : "default",
              }}
              >
              <Tooltip title={participant?.name || name || ""} placement="top" arrow enterDelay={50} leaveDelay={50} PopperProps={{ disablePortal: false, sx: { zIndex: 9999 } }} disableInteractive={false}>
                <Box
                  onClick={handleOpen}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                    minWidth: 0,
                    justifyContent: "flex-start",
                  }}
                >
                  {showName ? (
                    <Typography
                      sx={{
                        fontFamily: "var(--font-family)",
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#33333B",
                        lineHeight: "18px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "fit-content",
                      }}
                    >
                      {participant?.name || name || email}
                    </Typography>
                  ) : (
                    <Avatar
                      src={participant?.pictureUrl || ""}
                      alt={participant?.name || name || email}
                      sx={{
                        bgcolor: !participant?.pictureUrl ? getAvatarColor(participant?.email || participant?.name) : undefined,
                        width: 22,
                        height: 22,
                        fontSize: 10,
                        cursor: "pointer",
                      }}
                    >
                      {!participant?.pictureUrl && getAvatarInitials(participant?.email || participant?.name)}
                    </Avatar>
                  )}
                </Box>
              </Tooltip>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, }}>
                {showEditIcon && (isHovered) && (
                  <IconButton
                    size="small"
                    onClick={handleOpen}
                    sx={{ width: 24, height: 24 }}
                  >
                    <img src={EditIcon} style={{width: "20px", height:"20px"}} />
                  </IconButton>
                )}
                {showCrossIcon && (
                  <IconButton
                    className="cross-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onUpdate) {
                        onUpdate(row, null, [fieldName ?? ""]);
                      } else {
                        rowAction?.updateAssigneeCallback?.(row, null);
                      }
                    }}
                    sx={{ width: 20, height: 20 }}
                  >
                    <span>×</span>
                  </IconButton>
                )}
              </Box>
            </Box>
          ) : (
            <Tooltip title="Assign" placement="top" arrow enterDelay={300} leaveDelay={100} PopperProps={{ disablePortal: false, sx: { zIndex: 9999 } }} disableInteractive={false}>
              <Box
                onClick={handleOpen}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                {hideActionIcon ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontFamily: "var(--font-family)",
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#33333B",
                      lineHeight: "18px",
                      maxWidth: "100%",
                      paddingLeft: "10px"
                    }}
                  >
                    {name || participant?.name || email || "-"}
                  </Typography>

                  {showEditIcon && (isHovered || open) && (
                    <IconButton
                      size="small"
                      onClick={handleOpen}
                      sx={{ width: 24, height: 24 }}
                    >
                      <img
                        src={EditIcon}
                        alt="Edit"
                        style={{ width: "20px", height: "20px" }}
                      />
                    </IconButton>
                  )}
                </Box>
              ) : (
                <IconButton>
                  <img src={AddPersonIcon} alt="Add Person" />
                </IconButton>
              )}
              </Box>
            </Tooltip>
          )}

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{ style: { maxHeight: 300, minWidth: 250 } }}
            onClick={(e) => e.stopPropagation()}
            MenuListProps={{
              sx: {
                paddingTop: "0px !important",
                paddingBottom: "0px !important",
              },
            }}
          >
            <CustomDropdown<any>
              items={attendeeParticipants.map(normalizeParticipant)}
              value={
                email
                  ? normalizeParticipant({
                      name: participant?.name || name,
                      email,
                      pictureUrl: participant?.pictureUrl,
                    })
                  : null
              }
              onChange={(user) => {
                if (user) handleSelect(user);
                else rowAction.updateAssigneeCallback(row, null);
              }}
              placeholder="Search by name"
              maxHeight={200}
              showSearch
              filterFn={(p, search) => (p.name || "").toLowerCase().includes(search.toLowerCase())}
              renderItem={(user) => (
                <div className="flex items-center">
                  <ListItemAvatar sx={{minWidth: "40px"}}>
                  <Avatar
                    src={user?.pictureUrl || ""}
                    alt={user?.name || ""}
                    sx={{
                      bgcolor: !user?.pictureUrl ? getAvatarColor(user?.email || user?.name) : undefined,
                      width: 26,
                      height: 26,
                      fontSize: 10,
                    }}
                  >
                    {!user?.pictureUrl && getAvatarInitials(user?.email || user?.name)}
                  </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={user.name || ""} primaryTypographyProps={{ noWrap: true }} />
                </div>
              )}
            />
          </Menu>
        </>
      ) : (
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            padding: "4px 8px",
          }}>
          {(participant || name) && (
            <Tooltip title={participant?.name || name || ""} placement="top" arrow enterDelay={300} leaveDelay={100} PopperProps={{ disablePortal: false, sx: { zIndex: 9999 } }} disableInteractive={false}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    minWidth: 0,
                  }}
                >
                  {showName ? (
                    <Typography
                    noWrap
                    sx={{
                      fontFamily: "var(--font-family)",
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#33333B",
                      lineHeight: "18px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "auto"
                    }}
                  >
                      {participant?.name || name || email || "-"}
                    </Typography>
                  ) : (
                    <Avatar
                  src={participant?.pictureUrl || ""}
                  alt={participant?.name || name || undefined}
                  sx={{
                    bgcolor: !participant?.pictureUrl
                      ? getAvatarColor( participant?.email || participant?.name || name)
                      : undefined,
                    width: 22,
                    height: 22,
                    fontSize: 10,
                  }}
                >
                  {!participant?.pictureUrl && getAvatarInitials( participant?.email || participant?.name || name )}
                </Avatar>
                )}
              </Box>
            </Tooltip>
          ) }
        </Box>
      )}
    </Box>
  );
};

export default ActionItemsAssigneeCell;
